import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { addToCart, adoptGuestCartToScope, setCartScope } from '../lib/cart';
import { setQuoteRequestScope } from '../lib/quoteRequests';

const PENDING_CART_KEY = 'etc_pending_cart_add';
const IDLE_TIMEOUT_MS = 30 * 60 * 1000;
const WARNING_BEFORE_MS = 2 * 60 * 1000;
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'wheel'];

const AuthContext = createContext(null);

function getApiErrorMessage(payload, fallback) {
  return typeof payload?.error === 'string' && payload.error.trim() ? payload.error : fallback;
}

function normalizeAuthUser(payload) {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return null;
  const userId = String(payload.id || payload._id || '').trim();
  const email = String(payload.email || '').trim();
  if (!userId || !email) return null;
  return {
    id: userId,
    email,
    firstName: String(payload.firstName || '').trim(),
    lastName: String(payload.lastName || '').trim(),
    phone: String(payload.phone || '').trim(),
    avatar: String(payload.avatar || '').trim(),
  };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const idleTimeoutRef = useRef(null);
  const warningTimeoutRef = useRef(null);
  const warningToastRef = useRef(null);

  const refreshUser = useCallback(async () => {
    try {
      const res = await fetch('/api/auth/me', { credentials: 'include' });
      if (!res.ok) {
        setUser(null);
        return null;
      }
      const data = await res.json().catch(() => ({}));
      const nextUser = normalizeAuthUser(data?.user);
      setUser(nextUser);
      return nextUser;
    } catch (err) {
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    async function loadUser() {
      await refreshUser();
      if (mounted) {
        setIsLoading(false);
      }
    }
    loadUser();
    return () => {
      mounted = false;
    };
  }, [refreshUser]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (user?.id) {
      adoptGuestCartToScope(user.id);
      setQuoteRequestScope(user.id, { adoptLegacy: true });
      return;
    }

    setCartScope('guest');
    setQuoteRequestScope('guest');
  }, [user?.id]);

  const clearIdleWarning = useCallback(() => {
    if (warningTimeoutRef.current) {
      window.clearTimeout(warningTimeoutRef.current);
      warningTimeoutRef.current = null;
    }
    if (warningToastRef.current) {
      toast.dismiss(warningToastRef.current);
      warningToastRef.current = null;
    }
  }, []);

  const logout = useCallback(async () => {
    await fetch('/api/auth/logout', {
      method: 'POST',
      credentials: 'include',
    });
    setCartScope('guest');
    setQuoteRequestScope('guest');
    setUser(null);
  }, []);

  const resetIdleTimer = useCallback(() => {
    if (typeof window === 'undefined') return;

    if (idleTimeoutRef.current) {
      window.clearTimeout(idleTimeoutRef.current);
    }
    clearIdleWarning();

    idleTimeoutRef.current = window.setTimeout(() => {
      void logout();
    }, IDLE_TIMEOUT_MS);

    warningTimeoutRef.current = window.setTimeout(() => {
      if (!warningToastRef.current) {
        warningToastRef.current = toast.custom(
          (toastId) => (
            <div className="rounded-lg border border-[var(--dash-border)] bg-[var(--dash-surface)] px-4 py-3 shadow-lg">
              <p className="text-sm font-medium text-[var(--dash-text)]">You will be signed out soon due to inactivity.</p>
              <div className="mt-2 flex items-center gap-2">
                <button
                  type="button"
                  className="rounded bg-[var(--dash-main)] px-3 py-1.5 text-sm font-medium text-white"
                  onClick={() => {
                    toast.dismiss(toastId.id);
                    warningToastRef.current = null;
                    resetIdleTimer();
                  }}
                >
                  Stay signed in
                </button>
                <button
                  type="button"
                  className="rounded border border-[var(--dash-border)] px-3 py-1.5 text-sm font-medium text-[var(--dash-text)]"
                  onClick={() => {
                    toast.dismiss(toastId.id);
                    warningToastRef.current = null;
                    void logout();
                  }}
                >
                  Log out now
                </button>
              </div>
            </div>
          ),
          { duration: Infinity, id: 'auth-idle-warning' }
        );
      }
    }, Math.max(IDLE_TIMEOUT_MS - WARNING_BEFORE_MS, 0));
  }, [clearIdleWarning, logout]);

  useEffect(() => {
    if (typeof window === 'undefined' || isLoading || !user?.id) {
      if (idleTimeoutRef.current) {
        window.clearTimeout(idleTimeoutRef.current);
        idleTimeoutRef.current = null;
      }
      clearIdleWarning();
      return;
    }

    resetIdleTimer();
    ACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, resetIdleTimer, { passive: true });
    });

    return () => {
      if (idleTimeoutRef.current) {
        window.clearTimeout(idleTimeoutRef.current);
        idleTimeoutRef.current = null;
      }
      clearIdleWarning();
      ACTIVITY_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, resetIdleTimer);
      });
    };
  }, [clearIdleWarning, isLoading, resetIdleTimer, user?.id]);

  const drainPendingCart = useCallback(() => {
    if (typeof window === 'undefined') return;
    try {
      const raw = sessionStorage.getItem(PENDING_CART_KEY) || localStorage.getItem(PENDING_CART_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      const items = Array.isArray(parsed) ? parsed : [parsed];
      items
        .filter((item) => item && typeof item === 'object')
        .forEach((item) => addToCart(item, item.quantity || 1));
      sessionStorage.removeItem(PENDING_CART_KEY);
      localStorage.removeItem(PENDING_CART_KEY);
    } catch (err) {
      sessionStorage.removeItem(PENDING_CART_KEY);
      localStorage.removeItem(PENDING_CART_KEY);
    }
  }, []);

  const login = useCallback(async (payload) => {
    const res = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });

    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(getApiErrorMessage(data, 'Login failed'));
    }
    const nextUser = normalizeAuthUser(data?.user);
    if (!nextUser) {
      throw new Error('Login failed');
    }

    setUser(nextUser);
    setCartScope(nextUser.id, { adoptLegacy: true });
    setQuoteRequestScope(nextUser.id, { adoptLegacy: true });
    drainPendingCart();
    return nextUser;
  }, [drainPendingCart]);

  const signup = useCallback(async (payload) => {
    const res = await fetch('/api/auth/signup', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });

    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(getApiErrorMessage(data, 'Signup failed'));
    }
    const nextUser = normalizeAuthUser(data?.user);
    if (!nextUser) {
      throw new Error('Signup failed');
    }

    setUser(nextUser);
    setCartScope(nextUser.id, { adoptLegacy: true });
    setQuoteRequestScope(nextUser.id, { adoptLegacy: true });
    drainPendingCart();
    return nextUser;
  }, [drainPendingCart]);

  const updateProfile = useCallback(async (payload) => {
    const res = await fetch('/api/auth/profile', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(getApiErrorMessage(data, 'Failed to update profile'));
    }
    const nextUser = normalizeAuthUser(data?.user);
    if (!nextUser) {
      throw new Error('Failed to update profile');
    }
    setUser(nextUser);
    return nextUser;
  }, []);

  const uploadAvatar = useCallback(async (file) => {
    const fd = new FormData();
    fd.append('avatar', file);
    const res = await fetch('/api/auth/upload-avatar', {
      method: 'POST',
      credentials: 'include',
      body: fd,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(getApiErrorMessage(data, 'Failed to upload avatar'));
    }
    const nextUser = normalizeAuthUser(data?.user);
    if (!nextUser) {
      throw new Error('Failed to upload avatar');
    }
    setUser(nextUser);
    return nextUser;
  }, []);

  const value = useMemo(
    () => ({
      user,
      isLoading,
      isAuthenticated: Boolean(user),
      login,
      signup,
      logout,
      updateProfile,
      uploadAvatar,
      refreshUser,
    }),
    [user, isLoading, login, signup, logout, updateProfile, uploadAvatar, refreshUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
